import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MovieDetailModal from '../components/MovieDetailModal';
import { useToast } from '../context/ToastContext';
import {findExistingRankedPost, findViewerPostForMovie, hasMovieInWatchlist, findWatchlistEntryForMovie,} from '../utils/rankingBins';

const INITIAL_RECS_CACHE_MS = 15_000;
let initialRecommendationsPromise = null;
let initialRecommendationsCache = null;

function movieIdOf(movie) {
  const raw = movie?.id ?? movie?.tmdb_id;
  if (raw == null) return null;
  return String(raw);
}

function dedupeSections(rawSections) {
  const seen = new Set();
  return (rawSections || []).map((section) => {
    const movies = (section?.movies || []).filter((movie) => {
      const id = movieIdOf(movie);
      if (!id) return false;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    return { ...section, movies };
  });
}

export default function ForYouPage({
  isActive = true,
  viewerPosts = [],
  viewerList = [],
  onAddToWatchMovieList,
  onRemoveFromWatchlist,
  onBeginRanking,
}) {
  const { showToast } = useToast();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyIds, setBusyIds] = useState(new Set());
  const [modalMovie, setModalMovie] = useState(null);
  const shownIdsRef = useRef([]);
  const refreshCountRef = useRef(0);
  const fetchRecommendations = useCallback(async (excludeIds = [], count = 0) => {
    setLoading(true);
    setError(null);
    try {
      const cycle = count % 5;
      const isInitialLoad = cycle === 0 && excludeIds.length === 0;
      const cacheIsFresh =
        isInitialLoad &&
        initialRecommendationsCache &&
        Date.now() - initialRecommendationsCache.ts < INITIAL_RECS_CACHE_MS;

      const excludeParam = cycle !== 0 && excludeIds.length > 0
        ? `?exclude_ids=${excludeIds.join(',')}`
        : '';
      let data;
      if (cacheIsFresh) {
        data = initialRecommendationsCache.data;
      } else if (isInitialLoad) {
        if (!initialRecommendationsPromise) {
          initialRecommendationsPromise = (async () => {
            console.log('fetching URL:', '/api/recommendations');
            const res = await fetch('/api/recommendations', { credentials: 'include' });
            const payload = await res.json();
            if (!res.ok || !payload.ok) {
              throw new Error(payload.error || 'Failed to load recommendations');
            }
            initialRecommendationsCache = { data: payload, ts: Date.now() };
            return payload;
          })().finally(() => {
            initialRecommendationsPromise = null;
          });
        }
        data = await initialRecommendationsPromise;
      } else {
        console.log('fetching URL:', `/api/recommendations${excludeParam}`);
        const res = await fetch(`/api/recommendations${excludeParam}`, { credentials: 'include' });
        data = await res.json();
        if (!res.ok || !data.ok) throw new Error(data.error || 'Failed to load recommendations');
      }
      setSections(dedupeSections(data.sections || []));

      const newIds = (data.sections || []).flatMap(s => s.movies.map(m => m.id || m.tmdb_id));

      const merged = [...new Set([...shownIdsRef.current, ...newIds])];
      shownIdsRef.current = merged;
      refreshCountRef.current += 1;
    } catch (e) {
      setError((e && e.message) || 'Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  }, []);

  function refreshWithCurrentExcludes() {
    const count = refreshCountRef.current;
    const nonResetCount = count === 0 ? 1 : count;
    fetchRecommendations(shownIdsRef.current, nonResetCount);
  }

  const cycleInSections = useCallback((id) => {
    const normalizedTargetId = String(id);
    let shouldForceRefresh = false;
    setSections((prev) =>
      prev.map((s) => {
        const idx = s.movies.findIndex((m) => (m.id || m.tmdb_id) === id);
        if (idx === -1) return s;
        if (s.movies.length <= 6) {
          shouldForceRefresh = true;
          return s;
        }
        const movies = [...s.movies];
        const visibleIds = new Set(
          movies
            .slice(0, 6)
            .map((m) => movieIdOf(m))
            .filter((mid) => mid && mid !== normalizedTargetId)
        );
        const replacementIdx = movies.findIndex((m, i) => {
          if (i < 6) return false;
          const mid = movieIdOf(m);
          return !!mid && !visibleIds.has(mid);
        });
        if (replacementIdx === -1) {
          shouldForceRefresh = true;
          return s;
        }
        const [refreshed] = movies.splice(idx, 1);
        const shiftedReplacementIdx = replacementIdx > idx ? replacementIdx - 1 : replacementIdx;
        movies.splice(idx, 0, movies.splice(shiftedReplacementIdx, 1)[0]);
        movies.push(refreshed);
        return { ...s, movies };
      })
    );
    if (shouldForceRefresh) {
      refreshWithCurrentExcludes();
    }
  }, [fetchRecommendations]);


  const handleBookmark = useCallback(async (movie) => {
    const id = movie.id || movie.tmdb_id;
    if (busyIds.has(id)) return;
    setBusyIds((prev) => new Set(prev).add(id));
    try {
      const inWatchlist = hasMovieInWatchlist(viewerList, movie);
      if (inWatchlist) {
        const entry = findWatchlistEntryForMovie(viewerList, movie);
        if (entry?.id && onRemoveFromWatchlist) {
          await onRemoveFromWatchlist(entry.id);
          showToast('Removed from watchlist');
        }
      } else if (onAddToWatchMovieList) {
        await onAddToWatchMovieList(movie);
        showToast('Saved to watchlist');
        cycleInSections(id);
      }
    } finally {
      setBusyIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
    }
  }, [busyIds, viewerList, onAddToWatchMovieList, onRemoveFromWatchlist, showToast, cycleInSections]);

  // Initial load unchanged
  useEffect(() => {
    if (!isActive) return;
    fetchRecommendations([], 0);
  }, [isActive, fetchRecommendations]);

  // Button handler always reads latest values from refs
  const handleRefresh = useCallback(() => {
    const count = refreshCountRef.current;
    const cycle = count % 5;
    if (cycle === 0) {
      shownIdsRef.current = [];
    }
    fetchRecommendations(shownIdsRef.current, refreshCountRef.current);
  }, [fetchRecommendations]);

  const existingForModal = useMemo(
    () => (modalMovie ? findExistingRankedPost(viewerPosts, modalMovie) : null),
    [modalMovie, viewerPosts],
  );
  const modalViewerHasWatched = Boolean(
    modalMovie && findViewerPostForMovie(viewerPosts, modalMovie),
  );
  const modalInWatchlist = modalMovie ? hasMovieInWatchlist(viewerList, modalMovie) : false;
  const modalWatchlistEntry = modalMovie ? findWatchlistEntryForMovie(viewerList, modalMovie) : null;

  return (
    <>
      <section className="flique-feed-page flique-foryou-page">
        <header className="flique-feed-toolbar">
          <span className="flique-feed-toolbar-side" aria-hidden="true" />
          <h1 className="flique-feed-toolbar-title">For You</h1>
          <span className="flique-feed-toolbar-side" aria-hidden="true" />
        </header>
      
        {error && (
          <div className="alert alert-danger py-2 mb-3 flique-feed-alert" role="alert">
            {error}
          </div>
        )}

        {loading && (
          <div className="flique-feed-skeleton-card" aria-busy="true" aria-label="Loading recommendations">
            <div className="flique-feed-skeleton-row">
              <div className="flique-feed-skeleton-poster-thumb" aria-hidden="true" />
              <div className="flique-feed-skeleton-text">
                <div className="flique-feed-skeleton-line mb-2" />
                <div className="flique-feed-skeleton-line flique-feed-skeleton-line--short" />
              </div>
            </div>
          </div>
        )}

        {!loading && sections.length === 0 && !error && (
          <div className="flique-feed-empty">
            <p className="flique-feed-empty-text">
              Watch and rank more movies to get personalised recommendations.
            </p>
          </div>
        )}

        {!loading && sections.length > 0 && sections.map((section) => (
          <div key={section.title} className="mb-4">
            <h2 className="flique-foryou-section-title">{section.title}</h2>
            <div className="flique-search-results">
              {section.movies.slice(0, 6).map((movie) => {
                const id = movie.id || movie.tmdb_id;
                const titleLabel = movie.title + (movie.year != null ? ` (${movie.year})` : '');
                const inWatchlist = hasMovieInWatchlist(viewerList, movie);
                return (
                  <div key={id} className="flique-search-result d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="d-flex gap-3 align-items-center flex-grow-1 text-start bg-transparent border-0 p-0"
                      onClick={() => setModalMovie(movie)}
                      aria-label={`View ${titleLabel}`}
                    >
                      {movie.poster_url ? (
                        <img src={movie.poster_url} alt="" className="flique-search-poster" loading="lazy" decoding="async" />
                      ) : (
                        <div className="flique-search-poster flique-search-poster-empty" aria-hidden="true">—</div>
                      )}
                      <div className="fw-bold">
                        {movie.title}{movie.year != null ? ` (${movie.year})` : ''}
                      </div>
                    </button>
                    <button
                      type="button"
                      className="flique-foryou-action-btn flex-shrink-0"
                      onClick={() => cycleInSections(id)}
                      aria-label="Show a different movie"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M23 4v6h-6" /><path d="M1 20v-6h6" />
                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="flique-feed-engage-bookmark-btn flique-foryou-bookmark-btn flex-shrink-0"
                      onClick={() => handleBookmark(movie)}
                      disabled={busyIds.has(id)}
                      aria-pressed={inWatchlist}
                      aria-label={inWatchlist ? 'Remove from watchlist' : 'Save to watchlist'}
                    >
                      <span className="flique-feed-engage-bookmark-icon" aria-hidden="true">
                        {inWatchlist ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
                          </svg>
                        )}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
         <div className="flique-foryou-refresh-row">
            <button
              type="button"
              className="flique-foryou-refresh-btn"
              onClick={handleRefresh}
              disabled={loading}
            >
              {(refreshCountRef.current % 5 === 0) ? 'Show me more movies' : 'Show me different movies'}
            </button>
          </div>

      </section>

      <MovieDetailModal
        movie={modalMovie}
        existingPost={existingForModal}
        open={modalMovie != null}
        initialStep="detail"
        onClose={() => setModalMovie(null)}
        onBeginRanking={(watchedMovie) => {
          if (typeof onBeginRanking !== 'function') return;
          onBeginRanking({
            ...watchedMovie,
            onBackToTierSelection: () => {
              setModalMovie(watchedMovie?.selectedMovie || null);
            },
          });
        }}
        onAddToWatchMovieList={onAddToWatchMovieList}
        onRemoveFromWatchlist={onRemoveFromWatchlist}
        viewerHasWatchedMovie={modalViewerHasWatched}
        inWatchlist={modalInWatchlist}
        watchlistEntryId={modalWatchlistEntry?.id}
      />
    </>
  );
}
